import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import Section from './Section.js';
import heroMunicipal from '../assets/hero-municipal.jpg';
import brickWeeds from '../assets/brickwork_with_weeds.jpg';

const MunicipalPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Municipal Weed Control BC | Boultbee</title>
            <meta name="description" content="Municipal herbicide services for BC cities and districts. Sidewalk weed control, park turf management, sportsfield programs, and noxious weed compliance." />
            <link rel="canonical" href="https://www.weedmanager.com/industries/municipal" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Municipal Weed Control BC | Boultbee" />
            <meta property="og:description" content="Municipal herbicide services for BC cities and districts. Sidewalk weed control, park turf management, sportsfield programs, and noxious weed compliance." />
            <meta property="og:url" content="https://www.weedmanager.com/industries/municipal" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-municipal.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Municipal Weed Control BC | Boultbee" />
            <meta name="twitter:description" content="Municipal herbicide services for BC cities and districts. Sidewalk weed control, park turf management, sportsfield programs, and noxious weed compliance." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-municipal.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Municipal Weed Management", "item": "https://www.weedmanager.com/industries/municipal" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Municipal Weed Control",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": { "@type": "AdministrativeArea", "name": "British Columbia, Canada" },
                "description": "Municipal herbicide application services including sidewalk weed control, park turf management, sportsfield programs, median maintenance, and noxious weed compliance for BC cities and regional districts."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Municipal Weed Management"
            subtitle="Professional herbicide application for BC municipalities, cities, and regional districts"
            backgroundImage={heroMunicipal}
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">From sidewalk weed control to sportsfield turf programs, Boultbee helps local governments maintain clean, safe public spaces while meeting provincial noxious weed compliance requirements.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
          <Section
            heading="Sidewalk, Hardscape & Infrastructure Weed Control"
            paragraph="Weeds in sidewalk joints, brickwork seams, and curb lines create tripping hazards, accelerate infrastructure deterioration, and generate public complaints. Boultbee provides precision herbicide treatment for pavement joints, expansion cracks, median curbing, guardrail bases, and fence lines. Our targeted application methods minimize product use while eliminating vegetation that damages municipal infrastructure. Programs can be scheduled seasonally or on a multi-visit rotation to maintain clean appearance year-round."
            imgSrc={brickWeeds}
            altText="Weeds growing through brick and hardscape joints requiring municipal herbicide treatment"
            imgWidth={800}
            imgHeight={600}
          />
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Park & Sportsfield Turf Management</h2>
              <p className="text-gray-700">Municipal parks, sportsfields, and school grounds require selective herbicide programs that eliminate broadleaf weeds and invasive grasses without damaging the turf. Boultbee designs site-specific turf management programs that target problem species at optimal growth stages for maximum control. We work with municipal parks departments to schedule applications around public use and provide all required public notification documentation.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Noxious Weed Compliance</h2>
              <p className="text-gray-700">BC municipalities are required under the Weed Control Act to manage designated noxious weed species on municipal land. Boultbee provides targeted herbicide treatment for regulated species including knapweed, Canada thistle, Scotch broom, and other designated plants. We survey municipal properties, map infestations using GPS, apply appropriate herbicides at optimal timing, and can provide compliance documentation upon request for provincial reporting.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Industries We Serve */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Industries We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/industries/railroad" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 18h8M8 18l-2 3m10-3 2 3M6 6h12v8a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V6Zm0 0V4h12v2M9 10h6" /></svg>
                  <h3 className="font-bold text-gray-900">Railroads</h3>
                  <p className="text-gray-700 text-sm mt-1">High-rail application & ballast control</p>
                </Link>
                <Link to="/industries/oil-gas" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 2C12 2 7 8.5 7 13a5 5 0 0 0 10 0c0-4.5-5-11-5-11Z" /></svg>
                  <h3 className="font-bold text-gray-900">Oil & Gas</h3>
                  <p className="text-gray-700 text-sm mt-1">Refineries, pipelines & tank farms</p>
                </Link>
                <Link to="/industries/utility" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" /></svg>
                  <h3 className="font-bold text-gray-900">Utilities</h3>
                  <p className="text-gray-700 text-sm mt-1">Substations, corridors & cable strips</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">We serve municipalities across British Columbia with flexible program options. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us to discuss your municipality's needs.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default MunicipalPage;
